import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { transferenciaService, Transferencia, TransferenciaEnvio, TransferenciaRecepcion } from '../../services/transferenciaService';
import { useAuthStore } from '../../store/authStore';

interface TransferenciaDetailProps {
  transferencia: Transferencia;
  onClose: () => void;
}

const TransferenciaDetail = ({ transferencia, onClose }: TransferenciaDetailProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [cantidadesEnviadas, setCantidadesEnviadas] = useState<{ [key: number]: number }>({});
  const [cantidadesRecibidas, setCantidadesRecibidas] = useState<{ [key: number]: number }>({});

  const enviarMutation = useMutation(
    (data: TransferenciaEnvio) => transferenciaService.enviar(transferencia.id, data),
    {
      onSuccess: () => {
        toast.success('Transferencia enviada correctamente');
        queryClient.invalidateQueries(['transferencias']);
        queryClient.invalidateQueries(['existencias']);
        queryClient.invalidateQueries(['movimientos']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al enviar transferencia');
      }
    }
  );

  const recibirMutation = useMutation(
    (data: TransferenciaRecepcion) => transferenciaService.recibir(transferencia.id, data),
    {
      onSuccess: () => {
        toast.success('Transferencia recibida correctamente');
        queryClient.invalidateQueries(['transferencias']);
        queryClient.invalidateQueries(['existencias']);
        queryClient.invalidateQueries(['movimientos']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al recibir transferencia');
      }
    }
  );

  const handleEnviar = () => {
    const detalles = transferencia.detalles?.map(detalle => ({
      productoId: detalle.productoId,
      cantidadEnviada: cantidadesEnviadas[detalle.productoId] || detalle.cantidadSolicitada
    })) || [];

    enviarMutation.mutate({ detalles });
  };

  const handleRecibir = () => {
    const detalles = transferencia.detalles?.map(detalle => ({
      productoId: detalle.productoId,
      cantidadRecibida: cantidadesRecibidas[detalle.productoId] || detalle.cantidadEnviada
    })) || [];

    recibirMutation.mutate({ detalles });
  };

  const getEstadoColor = (estado: string) => {
    const colors: { [key: string]: string } = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      en_transito: 'bg-blue-100 text-blue-800',
      recibida: 'bg-green-100 text-green-800',
      cancelada: 'bg-red-100 text-red-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Transferencia #{transferencia.id}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Origen</p>
              <p className="font-medium">{transferencia.sucursalOrigen?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Destino</p>
              <p className="font-medium">{transferencia.sucursalDestino?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Estado</p>
              <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(transferencia.estado)}`}>
                {transferencia.estado.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Creada por</p>
              <p className="font-medium">{transferencia.usuarioOrigen?.name}</p>
            </div>
            {transferencia.fechaEnvio && (
              <div>
                <p className="text-sm text-gray-600">Fecha Envío</p>
                <p className="font-medium">{new Date(transferencia.fechaEnvio).toLocaleString()}</p>
              </div>
            )}
            {transferencia.fechaRecepcion && (
              <div>
                <p className="text-sm text-gray-600">Fecha Recepción</p>
                <p className="font-medium">{new Date(transferencia.fechaRecepcion).toLocaleString()}</p>
              </div>
            )}
          </div>

          {transferencia.notas && (
            <div>
              <p className="text-sm text-gray-600">Notas</p>
              <p className="text-sm">{transferencia.notas}</p>
            </div>
          )}

          <div>
            <h3 className="font-semibold mb-2">Productos</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Producto</th>
                    <th className="px-4 py-2 text-left">Solicitado</th>
                    <th className="px-4 py-2 text-left">Enviado</th>
                    <th className="px-4 py-2 text-left">Recibido</th>
                    <th className="px-4 py-2 text-left">Costo Unit.</th>
                    {transferencia.estado === 'pendiente' && (
                      <th className="px-4 py-2 text-left">Cantidad a Enviar</th>
                    )}
                    {transferencia.estado === 'en_transito' && (
                      <th className="px-4 py-2 text-left">Cantidad a Recibir</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transferencia.detalles?.map((detalle) => (
                    <tr key={detalle.id}>
                      <td className="px-4 py-2">
                        {detalle.producto?.name} {detalle.producto?.sku && `(${detalle.producto.sku})`}
                      </td>
                      <td className="px-4 py-2">{detalle.cantidadSolicitada.toFixed(3)} {detalle.producto?.unidadMedida}</td>
                      <td className="px-4 py-2">{detalle.cantidadEnviada.toFixed(3)} {detalle.producto?.unidadMedida}</td>
                      <td className="px-4 py-2">{detalle.cantidadRecibida.toFixed(3)} {detalle.producto?.unidadMedida}</td>
                      <td className="px-4 py-2">${detalle.costoUnitario.toFixed(2)}</td>
                      {transferencia.estado === 'pendiente' && (
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            max={detalle.cantidadSolicitada}
                            defaultValue={detalle.cantidadSolicitada}
                            onChange={(e) => setCantidadesEnviadas({
                              ...cantidadesEnviadas,
                              [detalle.productoId]: parseFloat(e.target.value) || detalle.cantidadSolicitada
                            })}
                            className="w-24 px-2 py-1 border rounded text-sm"
                          />
                        </td>
                      )}
                      {transferencia.estado === 'en_transito' && (
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            max={detalle.cantidadEnviada}
                            defaultValue={detalle.cantidadEnviada}
                            onChange={(e) => setCantidadesRecibidas({
                              ...cantidadesRecibidas,
                              [detalle.productoId]: parseFloat(e.target.value) || detalle.cantidadEnviada
                            })}
                            className="w-24 px-2 py-1 border rounded text-sm"
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            {transferencia.estado === 'pendiente' && (
              <button
                onClick={handleEnviar}
                disabled={enviarMutation.isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {enviarMutation.isLoading ? 'Enviando...' : 'Enviar Transferencia'}
              </button>
            )}
            {transferencia.estado === 'en_transito' && (
              <button
                onClick={handleRecibir}
                disabled={recibirMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {recibirMutation.isLoading ? 'Recibiendo...' : 'Recibir Transferencia'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferenciaDetail;

